import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Iter-95 — generic, non-disruptive polling hook.
 *
 *   const { lastUpdated, refresh } = usePolling(
 *     async ({ signal }) => { ... fetch + merge state ... },
 *     { intervalMs: 12000, enabled: true }
 *   );
 *
 * Behaviour:
 *  · Calls `fetcher` every `intervalMs` (default 12s).
 *  · Uses `setTimeout` chained to itself, so requests never overlap.
 *  · Pauses while the document is hidden (visibilitychange) — picks
 *    back up immediately when the tab becomes visible again, so the
 *    user sees fresh data the moment they return to the tab.
 *  · Skips ticks while `navigator.onLine === false`.
 *  · Never throws into the parent — caller's fetcher is wrapped so a
 *    transient network error simply yields a stale render until the
 *    next tick.
 *  · `fetcher` receives a tick context with `silent: true` so callers
 *    can skip showing loading spinners during background refreshes.
 *  · Returns `refresh()` for an immediate manual fetch (e.g. on a
 *    user-tapped "↻ refresh" affordance).
 *
 * The hook is intentionally NOT in charge of the *initial* load —
 * pages keep their existing `useEffect` for the first paint, and only
 * lean on this hook for ongoing background polling. This keeps the
 * loading semantics on first paint identical to before.
 */
export default function usePolling(fetcher, options = {}) {
    const { intervalMs = 12000, enabled = true } = options;
    const [lastUpdated, setLastUpdated] = useState(null);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;
    const timerRef = useRef(null);
    const inFlightRef = useRef(false);
    const cancelledRef = useRef(false);

    const tick = useCallback(async () => {
        if (cancelledRef.current) return;
        // Skip while hidden / offline. We schedule the next attempt
        // anyway so visibility/online resume catches up automatically.
        if (
            (typeof document !== "undefined" && document.visibilityState === "hidden") ||
            (typeof navigator !== "undefined" && navigator.onLine === false)
        ) {
            scheduleNext();
            return;
        }
        if (inFlightRef.current) {
            scheduleNext();
            return;
        }
        inFlightRef.current = true;
        try {
            await fetcherRef.current({ silent: true });
            if (!cancelledRef.current) setLastUpdated(new Date());
        } catch {
            /* non-fatal — keep stale data, retry next tick */
        } finally {
            inFlightRef.current = false;
            scheduleNext();
        }
    }, []);

    const scheduleNext = useCallback(() => {
        if (cancelledRef.current) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(tick, intervalMs);
    }, [tick, intervalMs]);

    const refresh = useCallback(async () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        await tick();
    }, [tick]);

    useEffect(() => {
        if (!enabled) return undefined;
        cancelledRef.current = false;
        scheduleNext();
        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                // Coming back to the tab — bring the page up to date now
                // rather than waiting for the next tick.
                refresh();
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("online", refresh);
        return () => {
            cancelledRef.current = true;
            if (timerRef.current) clearTimeout(timerRef.current);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("online", refresh);
        };
    }, [enabled, scheduleNext, refresh]);

    return { lastUpdated, refresh };
}
